"use client";

import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const videoVariants: Variants = {
  hidden: { opacity: 0, scale: 0.82, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  },
};

export const VideoSummarySection = (): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.4 },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative flex w-full flex-col items-center overflow-hidden bg-black px-4 py-16 sm:px-8 sm:py-24 lg:py-32">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,97,254,0.18)_0%,rgba(0,0,0,0)_100%)] blur-3xl" />

      <div className="relative z-10 flex w-full max-w-[1066px] flex-col items-center gap-10">
     

        <motion.div
          className="relative w-full origin-center will-change-transform"
          variants={videoVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          <div className="pointer-events-none absolute -inset-px rounded-[24px] bg-[linear-gradient(180deg,rgba(0,97,254,0.6)_0%,rgba(218,232,255,0.15)_50%,rgba(0,97,254,0.4)_100%)] opacity-70" />
          <div className="relative overflow-hidden rounded-[23px] border border-[#ffffff1a] bg-[#ffffff08] p-1.5 backdrop-blur-[52px]">
            <video
              ref={videoRef}
              className="block h-auto w-full rounded-[17px] bg-black"
              controls
              autoPlay
              muted
              playsInline
              preload="auto"
            >
              <source
                src="/hong-kong-company-formation-v2/summary.mp4"
                type="video/mp4"
              />
            </video>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
